/**
 * @author Nihar <nihar.baishya@314ecorp.com>
 * @description ClaimWorkListActions
 */
import _ from 'lodash';
import { Dropdown, Button, App, ButtonProps, Tooltip } from 'antd';
import { MoreOne } from '@icon-park/react';
import { useNavigate } from 'react-router-dom';
import { MenuItemType } from 'antd/es/menu/interface';

import useDeleteWorkList from 'hooks/claim/mutate/useDeleteWorkList';
import { getEditClaimWorkListPath, getNewWorkListPath } from 'utils/router';
import { isWorklistAssignedToUser } from 'utils/claims/workLists';
import { useAppStoreValues } from 'store/app';
import { NO_ACCESS_TOOLTIP_TEXT } from '@veritable/common/constants';
import { WORKLIST_TYPES } from 'app-constants/claims/workLists';

export interface ClaimWorkListActionsProps extends ButtonProps {
	claimWorkList: ClaimWorkLists.IWorkListResponse;
	onEdit?: () => void;
	onDuplicate?: () => void;
	onDelete?: () => void;
	iconOnly?: boolean;
}

const getContent = (claimsCount: number) => {
	if (claimsCount > 0) {
		return (
			<>
				{`This worklist contains ${claimsCount} active claims. Are you sure you want to permanently delete this worklist?`}
				<br />
				{'This action cannot be undone.'}
			</>
		);
	}
	return (
		<>
			{'Are you sure you want to permanently delete this worklist?'}
			<br />
			<br />
			{'This action cannot be undone.'}
		</>
	);
};

const ClaimWorkListActions: React.FC<ClaimWorkListActionsProps> = ({ claimWorkList, onEdit, onDuplicate, onDelete, ...rest }) => {
	const { modal } = App.useApp();
	const { mutateAsync: deleteWorkList } = useDeleteWorkList();
	const navigate = useNavigate();
	const { user, userAccess } = useAppStoreValues();
	const { canAddWorklists, canDeleteWorklists, isBillingUser } = userAccess;

	const isMyWorklist = isWorklistAssignedToUser(claimWorkList, user);
	const isWorklistManual = claimWorkList?.type === WORKLIST_TYPES.manual.key;

	// Billing users can only edit "manual" worklists assigned to them; admins can do all
	const canEditThisWorklist = !isBillingUser || (isMyWorklist && isWorklistManual);

	const isAnyButtonActionable = canEditThisWorklist || canAddWorklists || canDeleteWorklists;
	const confirmDeleteWorkList = () => {
		const claimsCount = claimWorkList?.metadata?.claimsCount;

		const { update, destroy } = modal.confirm({
			title: 'Delete Worklist',
			width: 560,
			content: getContent(claimsCount),
			okButtonProps: { danger: true },
			onOk: async () => {
				update({ okButtonProps: { loading: true } });
				await deleteWorkList(
					{ entityType: claimWorkList.entityType, id: claimWorkList?.id },
					{
						onSuccess: () => {
							if (_.isFunction(onDelete)) {
								onDelete();
							}
							destroy();
						},
						onError: destroy,
					},
				);
				return true;
			},
			okText: 'Delete',
			cancelText: 'Cancel',
		});
	};

	const schema: MenuItemType[] = [
		{
			key: 'edit',
			label: (
				<Tooltip title={canEditThisWorklist ? '' : NO_ACCESS_TOOLTIP_TEXT}>
					{isWorklistManual ? 'Update Claims' : 'Update Rules'}
				</Tooltip>
			),
			disabled: !canEditThisWorklist,
			onClick: ({ domEvent }) => {
				domEvent.stopPropagation();
				navigate(getEditClaimWorkListPath(claimWorkList?.id));
				if (_.isFunction(onEdit)) {
					onEdit();
				}
			},
		},
		...(!isWorklistManual
			? [
					{
						key: 'duplicate',
						label: <Tooltip title={canAddWorklists ? '' : NO_ACCESS_TOOLTIP_TEXT}>{'Duplicate'}</Tooltip>,
						disabled: !canAddWorklists,
						onClick: ({ domEvent }) => {
							domEvent.stopPropagation();
							navigate(getNewWorkListPath(claimWorkList?.type, claimWorkList?.id));
							if (_.isFunction(onDuplicate)) {
								onDuplicate();
							}
						},
					},
				]
			: []),
		{
			key: 'delete',
			label: <Tooltip title={canDeleteWorklists ? '' : NO_ACCESS_TOOLTIP_TEXT}>{'Delete'}</Tooltip>,
			disabled: !canDeleteWorklists,
			danger: true,
			onClick: ({ domEvent }) => {
				domEvent.stopPropagation();
				confirmDeleteWorkList();
			},
		},
	];

	return (
		<Dropdown menu={{ items: schema }} placement='bottomRight' trigger={['click']} disabled={!isAnyButtonActionable}>
			<Tooltip title={isAnyButtonActionable ? '' : NO_ACCESS_TOOLTIP_TEXT}>
				<Button
					color='default'
					icon={<MoreOne />}
					onClick={(e) => e.stopPropagation()}
					{...rest}
					disabled={!isAnyButtonActionable}
				/>
			</Tooltip>
		</Dropdown>
	);
};

export default ClaimWorkListActions;
